// MetricCard.tsx — Universal metric tile for the Overview and other sections.
//
// Renders the premium-surface card pattern:
//   - card-label-text (Cinzel uppercase eyebrow)
//   - card-trend (Cinzel uppercase chip — up / flat / down variants)
//   - card-numeral (Cinzel Decorative 700 — the load-bearing data atom)
//   - card-numeral-sub (optional sub-unit suffix: "days", "%", ".53")
//   - card-caption (Cormorant italic body copy)
//
// Status variants: is-warn and is-crit shift only the numeral color.
// Chrome (card background, stroke, shadow) is identical across variants.
// This ensures status is a tonal nudge, never a panel-color reskin. (DESIGN.md §2)

import React from 'react';

export type CardStatus = 'default' | 'warn' | 'crit';
export type TrendDir = 'up' | 'flat' | 'down';

interface MetricCardProps {
  label: string;
  numeral: string;
  numeralSub?: string;
  caption: string;
  trend?: string;
  trendDir?: TrendDir;
  status?: CardStatus;
  className?: string;
}

export default function MetricCard({
  label,
  numeral,
  numeralSub,
  caption,
  trend,
  trendDir = 'up',
  status = 'default',
  className = '',
}: MetricCardProps): React.ReactElement {
  const statusClass = status === 'warn' ? 'is-warn' : status === 'crit' ? 'is-crit' : '';
  const trendClass = trendDir === 'flat' ? 'is-flat' : trendDir === 'down' ? 'is-down' : '';

  return (
    <article className={`card ${statusClass} ${className}`.trim()}>
      <div className="card-label">
        <span className="card-label-text">{label}</span>
        {trend !== undefined && (
          <span className={`card-trend ${trendClass}`.trim()}>{trend}</span>
        )}
      </div>
      <div className="card-numeral">
        {numeral}
        {numeralSub !== undefined && (
          <span className="card-numeral-sub">{numeralSub}</span>
        )}
      </div>
      <p className="card-caption">{caption}</p>
    </article>
  );
}
